import { observer } from 'mobx-react-lite'
import { EnumHelperText, InputCheckbox, TextFieldWithLabel, Typography } from 'shared/ui'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'

import styles from './styles.module.scss'

export const ContactInfo = observer(() => {
  const {
    navigationStore,
    contactInfoStore: {
      firstName,
      lastName,
      businessTitle,
      businessEmail,
      businessEmailError,
      businessEmailDomainWarning,
      websiteUrl,
      websiteUrlError,
      consent,
      isWebsiteUrlValid,
      isNextDisabled,
      setFirstName,
      setLastName,
      setBusinessTitle,
      setBusinessEmail,
      setWebsiteUrl,
      toggleConsent,
      validateBusinessEmail,
      validateWebsiteUrl,
    },
  } = useRcsWizardContext()

  const onNext = () => {
    navigationStore.goToNextStep(RcsWizardSteps.contactInfo)
  }

  return (
    <div className={styles.wrap}>
      <PageDescription>
        We will contact your authorized representative to verify your identities. Please ensure that
        they are contactable via email and phone.
      </PageDescription>

      <Typography
        className={styles.sectionTitle}
        variant='body-lg-medium'
        ariaLabel='ContactInfo_authorizedUser'
      >
        Authorized user
      </Typography>

      <div className={styles.nameRow}>
        <TextFieldWithLabel
          marginBottom={0}
          widthWrap='100%'
          labelProps={{ label: { text: 'First name' } }}
          textFieldProps={{
            value: firstName,
            onChange: setFirstName,
            InputProps: { placeholder: 'e.g. Adam' },
          }}
        />
        <TextFieldWithLabel
          marginBottom={0}
          widthWrap='100%'
          labelProps={{ label: { text: 'Last name' } }}
          textFieldProps={{
            value: lastName,
            onChange: setLastName,
            InputProps: { placeholder: 'e.g. Johnson' },
          }}
        />
      </div>

      <TextFieldWithLabel
        marginTop={3}
        marginBottom={0}
        labelProps={{
          label: { text: 'Business title' },
          questionTooltipProps: {
            type: 'description',
            width: 247,
            label: 'We’re just making sure you’re in a position to make important decisions.',
            desc: 'So, president, founder, executive, manager, or even boss are legit options.',
          },
        }}
        textFieldProps={{
          value: businessTitle,
          onChange: setBusinessTitle,
          InputProps: { placeholder: 'e.g. Founder' },
        }}
      />

      <TextFieldWithLabel
        marginTop={3}
        marginBottom={0}
        errorBorder={Boolean(businessEmailError)}
        errorMessage={businessEmailError}
        helperTextProps={
          businessEmailDomainWarning
            ? {
                variant: EnumHelperText.Warning,
                text: businessEmailDomainWarning,
                icon: 'alertTriangleYellow',
              }
            : undefined
        }
        labelProps={{ label: { text: 'Business email' } }}
        textFieldProps={{
          value: businessEmail,
          onChange: setBusinessEmail,
          onBlur: validateBusinessEmail,
          InputProps: { placeholder: 'e.g. adam.johnson@example.com' },
        }}
      />

      <TextFieldWithLabel
        marginTop={3}
        marginBottom={0}
        errorBorder={Boolean(websiteUrlError)}
        errorMessage={websiteUrlError}
        labelProps={{
          label: { text: 'Business website URL' },
          questionTooltipProps: {
            type: 'description',
            width: 240,
            label: 'Business website URL',
          },
        }}
        textFieldProps={{
          value: websiteUrl,
          onChange: setWebsiteUrl,
          onBlur: validateWebsiteUrl,
          InputProps: { placeholder: 'e.g. https://www.example.com/' },
        }}
        rightActions={[
          makeTextFieldIcon({
            icon: isWebsiteUrlValid ? 'check2' : undefined,
            fontSize: 16,
            color: 'var(--green-60)',
          }),
        ]}
      />

      <InputCheckbox
        className={styles.consent}
        checked={consent}
        onChecked={toggleConsent}
        customLabel={
          <Typography
            ariaLabel='ContactInfo_consent'
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
          >
            I confirm that my nominated authorized representative agree to be contacted by Salesmsg
          </Typography>
        }
      />

      <NextButton text='Next' disabled={isNextDisabled} onClick={onNext} />
    </div>
  )
})
